import type { Networkish, SupWeb3VaultBalance } from "@/types";
import { useMemo } from "react";

import useSupportedChains from "@/hooks/api/useSupportedChains";
import { getProtocolImage, getProtocolName } from "@/utils";
import { FixedNumber } from "@ethersproject/bignumber";

export type PortfolioWidgetsGroupBy = "protocols" | "vault_chains" | "position_chains";

export interface PortfolioSummarySingleWidget {
  name: string;
  badge: string;
  value: FixedNumber;
  usdValue: number;
}

export default function usePortfolioWidgets(superPositions: SupWeb3VaultBalance[], groupBy: PortfolioWidgetsGroupBy) {
  const { data: supportedChains } = useSupportedChains();

  const { selectedChains, selectedProtocols } = useMemo(() => {
    let selectedChains = [] as Networkish[],
      selectedProtocols = [] as string[];

    superPositions?.forEach(singlePosition => {
      const protocolName = getProtocolName(singlePosition.vault);
      const chainId = groupBy === "position_chains" ? singlePosition.chain_id : singlePosition.vault.chain.id;
      selectedChains.includes(chainId) || selectedChains.push(chainId);
      selectedProtocols.includes(protocolName) || selectedProtocols.push(protocolName);
    });

    return {
      selectedChains,
      selectedProtocols,
    };
  }, [superPositions, groupBy]);

  return useMemo(
    () =>
      (
        (groupBy === "protocols"
          ? selectedProtocols.map(singleProtocol => {
              const filteredPositions = superPositions?.filter(singleBalance => getProtocolName(singleBalance.vault) === singleProtocol);
              if (filteredPositions === undefined || filteredPositions.length < 1) {
                return undefined;
              }
              return {
                name: singleProtocol,
                badge: getProtocolImage(filteredPositions[0].vault),
                value: filteredPositions.reduce((acc, singleBalance) => acc.addUnsafe(FixedNumber.from(`${singleBalance.superposition_balance}`)), FixedNumber.from(0)),
                usdValue: filteredPositions.reduce((acc, singleBalance) => acc - singleBalance.superposition_usd_value * -1, 0),
              } as PortfolioSummarySingleWidget;
            })
          : selectedChains.map(chainId => {
              const filteredPositions = superPositions?.filter(singleBalance =>
                groupBy === "position_chains" ? singleBalance.chain_id == chainId : singleBalance.vault.chain.id == chainId
              );
              if (filteredPositions === undefined || filteredPositions.length < 1) {
                return undefined;
              }

              const supportedChain = supportedChains?.find(chainInfo => chainInfo.chain_id === chainId);

              return {
                name: supportedChain?.name,
                badge: supportedChain?.icon,
                value: filteredPositions.reduce((acc, singleBalance) => acc.addUnsafe(FixedNumber.from(`${singleBalance.superposition_balance}`)), FixedNumber.from(0)),
                usdValue: filteredPositions.reduce((acc, singleBalance) => acc - singleBalance.superposition_usd_value * -1, 0),
              } as PortfolioSummarySingleWidget;
            })
        ).filter(singleWidget => singleWidget !== undefined) as PortfolioSummarySingleWidget[]
      ).sort((a, b) => a.name.localeCompare(b.name)),
    [groupBy, selectedProtocols, selectedChains, superPositions, supportedChains]
  );
}
